import React, { useEffect } from "react";
import { ArrowLeft, List } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom"; // Import useNavigate and Link
import Tabs from "@components/misc/Tabs";
import { toast } from "react-toastify";

import { useSelector, useDispatch } from "react-redux"; // Import useSelector and useDispatch
import { fetchIntegrationById, saveIntegrationAsync } from "@redux/slices/integrationSlice"; // Import the async thunk
import { parseError } from "../../utils/utils";
import { selectUserInfo } from "@redux/slices/authSlice"; // Import selectUserInfo for role-based access control
import tabsList from "@data/editor-tabs.json";

import OverviewDetails from "./Components/OverviewDetails";


// https://www.creative-tim.com/twcomponents/component/table-with-filter-and-search

const AddIntegration = () => {

  const dispatch = useDispatch();
  const integrationDetails = useSelector((state) => state.integration.selectedIntegration);
  const status = useSelector((state) => state.integration.status);
  const error = useSelector((state) => state.integration.error);
  const userInfo = useSelector(selectUserInfo); // Get user info from auth slice

  const { id } = useParams();

  useEffect(() => {
    dispatch(fetchIntegrationById(id));
  }, [dispatch, id]);

  const navigate = useNavigate(); // Get the navigate function

  const handleGoToDashboard = () => {
    navigate(-1);
  };

  const onTabSelected = (pageName) => {
    // Use replace:true so tab switching doesn't pollute browser history
    navigate(`/integrations/${id}/${pageName.toLowerCase()}`, { replace: true });
  }

  const handleGotoInvocations = () => {
    navigate(`/integrations/${id}/invocations`);
  };


  // ── Trigger-config validation ─────────────────────────────────────────────
  const validateTriggerConfig = () => {
    const raw = integrationDetails?.triggerDetails;
    let td = {};
    if (raw) {
      td = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : raw;
    }

    const triggerType = td?.triggerType;

    // Timer Trigger requires a cron expression
    if (triggerType === 'Timer Trigger') {
      if (!td?.triggerCronExpression?.trim()) {
        toast.error("Please enter a cron expression for the Timer Trigger before continuing.");
        return false;
      }
      return true;
    }

    // All other trigger types require a Request Payload Schema
    if (!td?.triggerRequestSchema?.trim()) {
      toast.error("Please provide a Request Payload Schema before continuing.");
      return false;
    }

    // Response Schema is required for all triggers except SNS, SQS, Azure SB, and Salesforce
    const noResponseSchema = ['SNS', 'SQS', 'AZURE_SB', 'SALESFORCE'];
    if (!noResponseSchema.includes(triggerType) && !td?.triggerResponseSchema?.trim()) {
      toast.error("Please provide a Response Schema before continuing.");
      return false;
    }

    return true;
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!integrationDetails || !integrationDetails.name || !integrationDetails.description) {
      toast.error("Please fill in both the Name and Description fields.");
      return;
    }
    if (!validateTriggerConfig()) return;
    try {
      const resultAction = await dispatch(saveIntegrationAsync({ id, integrationDetails, status: 'Draft' }));

      if (saveIntegrationAsync.fulfilled.match(resultAction)) {
        toast.success('Integration saved successfully!');
      } else {
        toast.error(resultAction.payload?.message || 'Failed to save integration.');
      }
    } catch (err) {
      toast.error(parseError(err));
    } finally {
      // setIsLoading(false);
    }
  }

  const handleGoNext = () => {
    if (!integrationDetails || !integrationDetails.name || !integrationDetails.description) {
      toast.error("Please fill in both the Name and Description fields.");
      return;
    }
    if (!validateTriggerConfig()) return;
    navigate(`/integrations/${id}/source`); // Navigate to the next page
  };

  return (
    <div className="mx-auto flex h-full min-h-0 max-w-full flex-col overflow-hidden bg-white">
      <section className="flex min-h-0 flex-1 flex-col">
        {/* FIX NAVBAR SCROLLING AND 3-COLUMN LAYOUT */}
        <div className="w-full border-b border-gray-100 bg-white px-4 sm:px-8 py-3">
          <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between">
            <div className="flex items-center gap-x-2 flex-shrink-0 justify-start min-w-[220px]">
              <button
                onClick={handleGoToDashboard}
                title={integrationDetails && integrationDetails.isNew ? "Back to builder" : "Back to listing"}
                className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[100px]"
              >
                <ArrowLeft className="h-5 w-5" />
                Back
              </button>

              {integrationDetails && integrationDetails.isNew === false &&
                <button
                  onClick={handleGotoInvocations}
                  title="Invocations"
                  className="flex items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-3 py-2 text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95"
                >
                  <List className="h-5 w-5" />
                </button>
              }

            </div>
            <div className="flex flex-1 items-center justify-center">
              <Tabs tabsList={tabsList} selectedIndex={0} interactive={integrationDetails?.isNew === false} onTabSelected={onTabSelected} />
            </div>
            <div className="flex items-center justify-end gap-x-2 flex-shrink-0 min-w-[220px]">
              <div className="flex flex-shrink-0 items-center gap-2">
                {/* Integrations shortcut */}
                <button
                  onClick={() => navigate('/integrations/listing')}
                  className="flex whitespace-nowrap items-center gap-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-5 py-2 text-xs font-bold text-[#1e3a8a] hover:bg-[#e0efff] hover:border-[#b8d9ff] transition-all shadow-sm active:scale-95"
                >
                  Integrations
                </button>
                {integrationDetails && integrationDetails.isNew === true &&
                  <button
                    onClick={() => navigate("/launch-builder")}
                    className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-6 py-2 text-xs font-semibold text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95 min-w-[120px]"
                  >
                    Cancel
                  </button>
                }
                {userInfo && userInfo.role !== 'user' &&
                  integrationDetails?.triggerDetails?.triggerType !== "None" && (
                    <button
                      onClick={handleSave}
                      className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-xs font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[120px]"
                    >
                      {integrationDetails && integrationDetails.isNew === false ? "Save" : "Save & Continue"}
                    </button>
                  )}
                {integrationDetails && integrationDetails.isNew === false &&
                  <button
                    onClick={handleGoNext}
                    className="flex items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-xs font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[120px]"
                  >
                    Next
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                }
              </div>
            </div>
          </div>
        </div>

        {/* FIX COMPONENT SCROLLING AND ALIGNMENT */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-12 mt-6">
          <div className="mx-auto w-full max-w-[1400px]">
            <OverviewDetails
              details={integrationDetails}
              isLoading={status === 'loading'}
              error={error}
              userRole={userInfo?.role}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddIntegration;
